import { ref, get, set, update, remove, onValue } from 'firebase/database';
import { database } from '../config/firebase';

export interface TemplateElement {
  id: string;
  type: 'image' | 'text' | 'shape' | 'sticker' | 'drawing' | 'background';
  x: number;
  y: number;
  width?: number;
  height?: number;
  src?: string;
  text?: string;
  color?: string;
  fontSize?: number;
  fontFamily?: string;
  layer?: number;
  rotation?: number;
  locked?: boolean;
  // Shape properties
  shapeType?: 'rectangle' | 'circle' | 'arrow' | 'line';
  strokeWidth?: number;
  strokeColor?: string;
  fillColor?: string;
  // Sticker properties
  emoji?: string;
  stickyColor?: string;
  // Drawing properties
  points?: { x: number; y: number }[];
  // Rich text properties
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  align?: 'left' | 'center' | 'right';
  // Background properties
  pattern?: 'none' | 'dots' | 'grid' | 'lines';
}

export interface MoodBoardTemplate {
  id: string;
  name: string;
  preview_image_url: string;
  elements: TemplateElement[];
  created_by: string;
  timestamp: number;
}

// Function to fetch all templates from Firebase
export const fetchTemplates = (callback: (templates: MoodBoardTemplate[]) => void) => {
  const templatesRef = ref(database, 'mood_board_templates');
  
  return onValue(templatesRef, (snapshot) => {
    try {
      const data = snapshot.val();
      if (data) {
        const templatesList = Object.entries(data).map(([id, template]: [string, any]) => ({
          id,
          ...template,
        }));
        callback(templatesList);
      } else {
        callback([]);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      callback([]);
    }
  });
};

// Function to fetch a single template by ID
export const fetchTemplateById = async (templateId: string): Promise<MoodBoardTemplate | null> => {
  try {
    const templateRef = ref(database, `mood_board_templates/${templateId}`);
    const snapshot = await get(templateRef);
    
    if (snapshot.exists()) {
      const data = snapshot.val();
      return {
        id: templateId,
        ...data,
        elements: Array.isArray(data.elements) ? data.elements : []
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching template:', error);
    throw error;
  }
};

// Function to create a new template
export const createTemplate = async (template: Omit<MoodBoardTemplate, 'id'>): Promise<string> => {
  const templateId = `template_${Date.now()}`;
  const templateRef = ref(database, `mood_board_templates/${templateId}`);
  
  try {
    await set(templateRef, {
      name: template.name,
      preview_image_url: template.preview_image_url,
      elements: template.elements,
      created_by: template.created_by,
      timestamp: template.timestamp
    });
    console.log(`Template created with ID: ${templateId}`);
    return templateId;
  } catch (error) {
    console.error('Error creating template:', error);
    throw error;
  }
};

// Function to update an existing template
export const updateTemplate = async (templateId: string, updates: Partial<MoodBoardTemplate>): Promise<void> => {
  const templateRef = ref(database, `mood_board_templates/${templateId}`);
  
  try {
    await update(templateRef, {
      ...updates,
      timestamp: Date.now()
    });
    console.log(`Template updated: ${templateId}`);
  } catch (error) {
    console.error('Error updating template:', error);
    throw error;
  }
};

// Function to delete a template
export const deleteTemplate = async (templateId: string): Promise<void> => {
  const templateRef = ref(database, `mood_board_templates/${templateId}`);
  
  try {
    await remove(templateRef);
    console.log(`Template deleted: ${templateId}`);
  } catch (error) {
    console.error('Error deleting template:', error);
    throw error;
  }
};

// Function to update a template with a specific layout
export const applyFashionMoodBoardLayout = async () => {
  const templateId = 'template_001';

  const newElements: TemplateElement[] = [
    // Background
    {
      id: 'bg-1',
      type: 'image',
      src: 'https://i.imgur.com/8z2kF2I.png', // Plain white background
      x: 0,
      y: 0,
      width: 800,
      height: 600,
      layer: 0,
    },
    // Images
    {
      id: 'img-1',
      type: 'image',
      src: 'https://i.imgur.com/O6L2aJz.jpg', // Blazer
      x: 40,
      y: 40,
      width: 215,
      height: 280,
      layer: 1,
    },
    {
      id: 'img-2',
      type: 'image',
      src: 'https://i.imgur.com/kP4mD6I.jpg', // Silk
      x: 275,
      y: 40,
      width: 220,
      height: 110,
      layer: 1,
    },
    {
      id: 'img-3',
      type: 'image',
      src: 'https://i.imgur.com/1nSo3yv.jpg', // Necklace
      x: 275,
      y: 170,
      width: 280,
      height: 180,
      layer: 1,
    },
    {
      id: 'img-4',
      type: 'image',
      src: 'https://i.imgur.com/z4b7xZ7.jpg', // Earring
      x: 40,
      y: 340,
      width: 215,
      height: 220,
      layer: 1,
    },
    {
      id: 'img-5',
      type: 'image',
      src: 'https://i.imgur.com/qL6S3yV.jpg', // Shoes
      x: 580,
      y: 230,
      width: 190,
      height: 120,
      layer: 1,
    },
    {
      id: 'img-6',
      type: 'image',
      src: 'https://i.imgur.com/R3So4y0.jpg', // Handbag
      x: 275,
      y: 370,
      width: 215,
      height: 190,
      layer: 1,
    },
    {
      id: 'img-7',
      type: 'image',
      src: 'https://i.imgur.com/vLp1b2k.jpg', // Sitting
      x: 510,
      y: 370,
      width: 260,
      height: 190,
      layer: 1,
    },
    // Color Swatches
    {
      id: 'swatch-1',
      type: 'image',
      src: 'https://i.imgur.com/zD13bL1.png', // Light beige
      x: 580,
      y: 170,
      width: 40,
      height: 40,
      layer: 2,
    },
    {
      id: 'swatch-2',
      type: 'image',
      src: 'https://i.imgur.com/A6mN2hV.png', // Grey
      x: 630,
      y: 170,
      width: 40,
      height: 40,
      layer: 2,
    },
    {
      id: 'swatch-3',
      type: 'image',
      src: 'https://i.imgur.com/o5e8d8L.png', // Charcoal
      x: 680,
      y: 170,
      width: 40,
      height: 40,
      layer: 2,
    },
    {
      id: 'swatch-4',
      type: 'image',
      src: 'https://i.imgur.com/Y1Z5k2j.png', // Darker beige
      x: 730,
      y: 170,
      width: 40,
      height: 40,
      layer: 2,
    },
    // Text
    {
      id: 'txt-1',
      type: 'text',
      text: 'Monochrome Mood',
      fontFamily: 'Playfair Display',
      fontSize: 34,
      color: '#1a1a1a',
      x: 515,
      y: 60,
      layer: 2,
    },
    {
      id: 'txt-2',
      type: 'text',
      text: '#Black #Minimal #Style',
      fontFamily: 'Lato',
      fontSize: 16,
      color: '#555555',
      x: 515,
      y: 120,
      layer: 2,
    },
  ];

  const updates: Partial<MoodBoardTemplate> = {
    elements: newElements,
    name: 'Monochrome Fashion Moodboard',
    preview_image_url: 'https://i.imgur.com/O6L2aJz.jpg',
  };

  try {
    await updateTemplate(templateId, updates);
    console.log(`Template ${templateId} updated successfully with the new layout.`);
  } catch (error) {
    console.error(`Error updating template ${templateId}:`, error);
    throw error;
  }
};

// Function to create initial templates for testing (optional)
export const createInitialTemplates = async () => {
  const initialTemplates: Omit<MoodBoardTemplate, 'id'>[] = [
    {
      name: 'Modern Living Room',
      preview_image_url: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop',
      created_by: 'Designer Pro',
      timestamp: Date.now(),
      elements: [
        {
          id: 'txt-1',
          type: 'text',
          text: 'Modern Living',
          x: 50,
          y: 50,
          color: '#2c3e50',
          fontSize: 32,
          fontFamily: 'Arial',
          layer: 1
        },
        {
          id: 'txt-2',
          type: 'text',
          text: 'Room Design',
          x: 50,
          y: 100,
          color: '#7f8c8d',
          fontSize: 24,
          fontFamily: 'Helvetica',
          layer: 2
        },
        {
          id: 'img-1',
          type: 'image',
          src: 'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?w=300&h=200&fit=crop',
          x: 200,
          y: 50,
          width: 200,
          height: 150,
          layer: 3
        }
      ]
    },
    {
      name: 'Kitchen Inspiration',
      preview_image_url: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=600&fit=crop',
      created_by: 'Kitchen Designer',
      timestamp: Date.now() - 86400000,
      elements: [
        {
          id: 'txt-1',
          type: 'text',
          text: 'Kitchen',
          x: 100,
          y: 80,
          color: '#34495e',
          fontSize: 36,
          fontFamily: 'Georgia',
          layer: 1
        },
        {
          id: 'txt-2',
          type: 'text',
          text: 'Inspiration',
          x: 100,
          y: 130,
          color: '#e74c3c',
          fontSize: 28,
          fontFamily: 'Georgia',
          layer: 2
        },
        {
          id: 'img-1',
          type: 'image',
          src: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=250&h=180&fit=crop',
          x: 300,
          y: 80,
          width: 250,
          height: 180,
          layer: 3
        }
      ]
    }
  ];

  try {
    for (const template of initialTemplates) {
      await createTemplate(template);
    }
    console.log('Initial templates created successfully!');
  } catch (error) {
    console.error('Error creating initial templates:', error);
    throw error;
  }
}; 